import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Checkbox, Input } from '@arco-design/web-react';
import styled from 'styled-components';

import type { Resource, TagFilter } from '../types';
import { getOptionText, parseTag } from '../utils';
import { LoadingItem } from './loading-item';
import { PopoverCard } from './popover-card';
import { StyleItem, StyleScrollbar } from './styles-base';

type CombineFilter = TagFilter & Partial<Resource>;

interface FilterMultiSelectProps {
  filter: CombineFilter;
  inputValue?: string;
  values?: Record<string, any>;
  onAddTag?: (tag: string, key: string, value: any) => void;
  onCloseDropdown?: () => void;
}

export function FilterMultiSelect({ filter, inputValue, values, onAddTag, onCloseDropdown }: FilterMultiSelectProps) {
  const { t } = useTranslation();
  const { options = [], loading = false } = filter;
  const containerRef = useRef<HTMLDivElement>(null);

  // 当前已选择的值
  const selectedValues = useMemo(() => {
    const currentValue = values?.[filter.key];
    if (Array.isArray(currentValue)) {
      return currentValue;
    }
    if (typeof currentValue === 'string') {
      return currentValue.split(',').filter(Boolean);
    }
    return [];
  }, [values, filter.key]);

  const [tempSelectedValues, setTempSelectedValues] = useState<string[]>(selectedValues);
  const [searchValue, setSearchValue] = useState<string>('');

  // 过滤选项
  const filteredOptions = useMemo(() => {
    if (!searchValue.trim()) return options;
    const keyword = searchValue.toLowerCase();
    return options.filter((option) => getOptionText(option).toLowerCase().includes(keyword));
  }, [options, searchValue]);

  useEffect(() => {
    setTempSelectedValues(selectedValues);
  }, [selectedValues]);

  const handleOptionClick = useCallback(
    (option: { value: string }) => {
      const newTempSelected = tempSelectedValues.includes(option.value)
        ? tempSelectedValues.filter((v) => v !== option.value)
        : [...tempSelectedValues, option.value];

      setTempSelectedValues(newTempSelected);
    },
    [tempSelectedValues],
  );

  // 失焦时确认选择
  const handleConfirm = useCallback(() => {
    const parsed = parseTag(inputValue || '');
    if (parsed && parsed.label && tempSelectedValues.length > 0) {
      const selectedOptions = options.filter((opt) => tempSelectedValues.includes(opt.value));
      const labels = selectedOptions.map((opt) => getOptionText(opt)).join(',');
      const tag = `${parsed.label}: ${labels}`;

      onAddTag?.(tag, filter.key, tempSelectedValues); // 直接传递数组
      // 注意：不需要手动调用 onCloseDropdown，因为 onAddTag 中已经处理了失焦和关闭
    } else {
      // 如果没有选择任何项，只关闭下拉栏
      onCloseDropdown?.();
    }
  }, [inputValue, tempSelectedValues, options, onAddTag, filter.key, onCloseDropdown]);

  // 监听外部点击
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      if (containerRef.current && !containerRef.current.contains(target)) {
        handleConfirm();
      }
    };

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        handleConfirm();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [handleConfirm]);

  if (options?.length === 0 && !loading) return null;
  return (
    <PopoverCard ref={containerRef}>
      <MenuContainer>
        <SearchInputWrapper>
          <Input
            placeholder={t('components:search.options')}
            value={searchValue}
            onChange={setSearchValue}
            allowClear
            disabled={loading}
            onMouseDown={(e) => e.stopPropagation()}
            onClick={(e) => e.stopPropagation()}
          />
        </SearchInputWrapper>
        <MenuList>
          {loading ? (
            <LoadingItem />
          ) : (
            <>
              {filteredOptions.map((option) => (
                <MenuItem key={option.value} selected={tempSelectedValues.includes(option.value)} onClick={() => handleOptionClick(option)}>
                  <StyledCheckbox checked={tempSelectedValues.includes(option.value)} onChange={() => handleOptionClick(option)} />
                  {option.label}
                </MenuItem>
              ))}
              {filteredOptions.length === 0 && <NoDataItem>{t('actions:search.option.not-found')}</NoDataItem>}
            </>
          )}
        </MenuList>
        {tempSelectedValues.length > 0 && !loading && (
          <ConfirmButton onClick={handleConfirm}>
            {t('actions:confirm.select')} ({tempSelectedValues.length})
          </ConfirmButton>
        )}
      </MenuContainer>
    </PopoverCard>
  );
}
/** ============================ styles ==================================== */
const MenuContainer = styled.div`
  display: flex;
  flex-direction: column;
  max-height: 350px;
`;

const SearchInputWrapper = styled.div`
  padding: 8px 12px;
  border-bottom: 1px solid var(--color-border);
  background-color: var(--color-bg-popup);
  flex-shrink: 0;
`;

const MenuList = styled(StyleScrollbar)`
  overflow-y: auto;
  max-height: 250px;
  overflow-x: hidden;
  border-radius: inherit;
  flex: 1;
`;

const MenuItem = styled(StyleItem)<{ selected?: boolean }>`
  display: flex;
  align-items: center;
  gap: 8px;
  background-color: ${(props) => (props.selected ? 'var(--color-fill-1)' : 'transparent')};
  cursor: pointer;

  &:hover {
    background-color: 'var(--color-fill-2)';
  }
`;

const StyledCheckbox = styled(Checkbox)`
  pointer-events: none;
`;

const NoDataItem = styled.div`
  padding: 8px 12px;
  color: var(--color-text-3);
  text-align: center;
`;

const ConfirmButton = styled.button`
  width: 100%;
  padding: 8px 12px;
  border: none;
  color: rgb(var(--primary-6));
  background-color: white;
  cursor: pointer;
  border-radius: 0 0 4px 4px;
  border-top: 1px solid #e5e5e5;
  margin-top: 4px;
  flex-shrink: 0;

  &:hover {
    background-color: rgb(var(--primary-6));
    color: white;
  }
`;
